/**
 * TPS-631 Update cart page and UpCart availability handling to keep messaging aligned
 *
 * This file owns the cart-page side of that behavior. It reads the availability
 * metadata rendered into each cart row, refreshes inventory-backed availability
 * messaging on load, enforces minimum-sale quantity rules, and prepares cart
 * change requests so the Availability line item property stays in sync with the
 * cart page, UpCart drawer, and PDP messaging.
 */
(function () {
  const productRequestCache = new Map();
  let didRunInitialSync = false;
  let cartUpdateUnsubscriber;
  let checkoutLockCount = 0;

  function normalizeAvailabilityText(value) {
    return String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function getCartItemsElement() {
    return document.querySelector("cart-items");
  }

  function getCheckoutButton() {
    return document.getElementById("checkout");
  }

  function isCartEmpty() {
    return Boolean(getCartItemsElement()?.classList.contains("is-empty"));
  }

  function syncCheckoutButtonState() {
    const checkoutButton = getCheckoutButton();
    if (!checkoutButton) return;

    const isLocked = checkoutLockCount > 0;
    checkoutButton.disabled = isLocked || isCartEmpty();
    checkoutButton.setAttribute("aria-busy", isLocked ? "true" : "false");
  }

  function beginCheckoutLock() {
    checkoutLockCount += 1;
    syncCheckoutButtonState();
  }

  function endCheckoutLock() {
    checkoutLockCount = Math.max(0, checkoutLockCount - 1);
    syncCheckoutButtonState();
  }

  function getCartRows() {
    const cartItems = getCartItemsElement();
    if (!cartItems) return [];
    return Array.from(cartItems.querySelectorAll("[data-cart-line]"));
  }

  function getRowByLine(line) {
    return getCartItemsElement()?.querySelector(`[data-cart-line="${line}"]`) || null;
  }

  function getLinePropertiesContainer(row) {
    return row?.querySelector("[data-cart-line-properties]") || null;
  }

  function getPropertyNodes(row) {
    return row ? Array.from(row.querySelectorAll("[data-cart-line-property]")) : [];
  }

  function getPropertyNode(row, key) {
    return getPropertyNodes(row).find((node) => node.dataset.propertyKey === key) || null;
  }

  function setTextContent(node, value) {
    node.textContent = value == null ? "" : String(value);
  }

  function readLineProperties(row) {
    const properties = {};
    getPropertyNodes(row).forEach((node) => {
      const key = node.dataset.propertyKey;
      if (!key) return;
      properties[key] = (node.textContent || "").trim();
    });
    return properties;
  }

  function setLineProperty(row, key, value) {
    const container = getLinePropertiesContainer(row);
    if (!container) return;

    let node = getPropertyNode(row, key);
    if (!node) {
      node = document.createElement("span");
      node.setAttribute("data-cart-line-property", "");
      node.dataset.propertyKey = key;
      container.appendChild(node);
    }

    setTextContent(node, value);
  }

  function getContractNodes(row) {
    return row ? Array.from(row.querySelectorAll("[data-cart-contract-value]")) : [];
  }

  function getContractNode(row, key) {
    return getContractNodes(row).find((node) => node.dataset.contractKey === key) || null;
  }

  function readContractValue(row, key) {
    const node = getContractNode(row, key);
    return node ? (node.textContent || "").trim() : "";
  }

  function setContractValue(row, key, value) {
    const node = getContractNode(row, key);
    if (!node) return;
    setTextContent(node, value);
  }

  function hasAvailabilityMetadata(row) {
    return row?.dataset.hasAvailabilityMetadata === "true" && !!row.querySelector("[data-cart-availability-contract]");
  }

  function getVisibleAvailabilityValue(row) {
    return row?.querySelector("[data-cart-availability-value]") || null;
  }

  function updateVisibleAvailability(row, value) {
    const availabilityValue = getVisibleAvailabilityValue(row);
    if (availabilityValue) {
      availabilityValue.textContent = value || "";
    }

    setLineProperty(row, "Availability", value || "");
    if (row) {
      row.dataset.currentAvailability = value || "";
    }
  }

  function getProductHandle(row) {
    if (row?.dataset.cartProductHandle) {
      return row.dataset.cartProductHandle;
    }

    const productUrl = row?.dataset.cartProductUrl || "";
    const marker = "/products/";
    const markerIndex = productUrl.indexOf(marker);
    if (markerIndex === -1) {
      return "";
    }

    return productUrl.slice(markerIndex + marker.length).split("?")[0].replace(/\/$/, "");
  }

  function getVariantId(row) {
    return String(row?.dataset.cartVariantId || "").trim();
  }

  function getQuantityInput(row) {
    return row?.querySelector(".quantity__input") || null;
  }

  function getCurrentQuantity(row) {
    const input = getQuantityInput(row);
    const value = Number(input ? input.value : row?.dataset.cartQuantity);
    return Number.isFinite(value) ? value : 0;
  }

  function getMinimumSaleQty(row) {
    const storedMinQty = Number(readContractValue(row, "_minimum_sale_qty"));
    if (Number.isFinite(storedMinQty) && storedMinQty > 0) {
      return storedMinQty;
    }

    const input = getQuantityInput(row);
    const inputMinQty = Number(input?.min);
    if (Number.isFinite(inputMinQty) && inputMinQty > 0) {
      return inputMinQty;
    }

    return 1;
  }

  function syncMinQtyControl(row) {
    const input = getQuantityInput(row);
    const minusButton = row?.querySelector('.quantity__button[name="minus"]');
    if (!input) return;

    const quantity = Number(input.value) || 0;
    const minimumSaleQty = getMinimumSaleQty(row);
    const shouldDisableMinus = quantity <= minimumSaleQty;
    const shouldLockManualEntry = minimumSaleQty > 1;
    const quantityMessage = `Quantity must be in multiples of ${minimumSaleQty}`;

    input.readOnly = shouldLockManualEntry;
    input.toggleAttribute("readonly", shouldLockManualEntry);
    if (shouldLockManualEntry) {
      input.title = quantityMessage;
      input.setAttribute("aria-label", quantityMessage);
    }

    if (minusButton) {
      minusButton.disabled = shouldDisableMinus;
      minusButton.classList.toggle("disabled", shouldDisableMinus);
      minusButton.setAttribute("aria-disabled", shouldDisableMinus ? "true" : "false");
    }

    row.dataset.cartQuantity = String(quantity);
  }

  function isSupportedRow(row) {
    return Boolean(row && hasAvailabilityMetadata(row) && getVisibleAvailabilityValue(row));
  }

  function getProductJson(handle) {
    if (!handle) {
      return Promise.reject(new Error("Missing product handle"));
    }

    if (!productRequestCache.has(handle)) {
      productRequestCache.set(
        handle,
        fetch(`/products/${handle}.js`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Product lookup failed for ${handle}`);
            }

            return response.json();
          })
          .catch((error) => {
            productRequestCache.delete(handle);
            throw error;
          })
      );
    }

    return productRequestCache.get(handle);
  }

  async function refreshVariantAvailableQuantity(row) {
    const handle = getProductHandle(row);
    const variantId = getVariantId(row);
    const contractAvailableQty = Number(readContractValue(row, "_variant_available_quantity"));
    const linePropertyAvailableQty = Number(readLineProperties(row)._variant_available_quantity);
    const fallbackAvailableQty = Number.isFinite(linePropertyAvailableQty) ? linePropertyAvailableQty : 0;

    if (!variantId) {
      return fallbackAvailableQty;
    }

    let nextAvailableQty = null;

    if (Number.isFinite(contractAvailableQty)) {
      nextAvailableQty = contractAvailableQty;
    }

    if (!Number.isFinite(nextAvailableQty) && typeof window.tpsGetLiveVariantInventoryQuantity === "function") {
      nextAvailableQty = window.tpsGetLiveVariantInventoryQuantity(variantId);
    }

    if (!Number.isFinite(nextAvailableQty) && handle) {
      try {
        const product = await getProductJson(handle);
        const variant = Array.isArray(product?.variants)
          ? product.variants.find((entry) => String(entry.id) === variantId)
          : null;
        const refreshedAvailableQty = Number(variant?.inventory_quantity);
        if (Number.isFinite(refreshedAvailableQty)) {
          nextAvailableQty = refreshedAvailableQty;
        }
      } catch (error) {
        console.error("customCartDetailsPageLogic: product refresh failed", error);
      }
    }

    if (!Number.isFinite(nextAvailableQty)) {
      console.warn("customCartDetailsPageLogic: could not resolve live variant inventory", {
        variantId,
        handle,
      });
      nextAvailableQty = fallbackAvailableQty;
    }

    setContractValue(row, "_variant_available_quantity", String(nextAvailableQty));
    return nextAvailableQty;
  }

  async function computeAvailabilityForRow(row, quantity, availableQtyOverride) {
    if (!isSupportedRow(row) || typeof window.getProductAvailabilityMessages !== "function") {
      return { supported: false, availabilityText: "" };
    }

    const availableQty = Number.isFinite(Number(availableQtyOverride))
      ? Number(availableQtyOverride)
      : Number(readContractValue(row, "_variant_available_quantity")) || 0;

    const { cartLineItemAvailabilityMessage, nodeText } = await window.getProductAvailabilityMessages(
      readContractValue(row, "_product_sku"),
      Number(quantity) || 0,
      availableQty,
      readContractValue(row, "_lead_time"),
      readContractValue(row, "_is_custom_order")
    );

    return {
      supported: true,
      availabilityText: normalizeAvailabilityText(cartLineItemAvailabilityMessage || nodeText),
    };
  }

  async function persistCartLineProperties(row, quantity, properties) {
    const itemKey = row?.dataset?.cartItemKey;
    if (!itemKey) {
      return null;
    }

    const cartChangeUrl = window?.routes?.cart_change_url || "/cart/change.js";

    try {
      const response = await fetch(cartChangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: itemKey,
          quantity,
          properties,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("customCartDetailsPageLogic: cart property update failed", response.status, data);
        return null;
      }

      return data;
    } catch (error) {
      console.error("customCartDetailsPageLogic: cart property update failed", error);
      return null;
    }
  }

  async function refreshAndSyncAvailabilityMessage(row) {
    if (!isSupportedRow(row)) {
      syncMinQtyControl(row);
      return;
    }

    const storedQty = readLineProperties(row)._variant_available_quantity ?? "";
    const storedAvailability =
      readLineProperties(row).Availability || row.dataset.currentAvailability || "";

    const refreshedAvailableQty = await refreshVariantAvailableQuantity(row);
    const quantity = getCurrentQuantity(row);
    const result = await computeAvailabilityForRow(row, quantity, refreshedAvailableQty);

    if (result.supported) {
      const refreshedQty = String(refreshedAvailableQty);
      const qtyChanged = refreshedQty !== String(storedQty ?? "");
      const messageChanged = result.availabilityText !== String(storedAvailability).trim();

      if (qtyChanged || messageChanged) {
        const properties = readLineProperties(row);
        properties._variant_available_quantity = refreshedQty;
        properties.Availability = result.availabilityText;
        await persistCartLineProperties(row, quantity, properties);
      }

      updateVisibleAvailability(row, result.availabilityText);
    }

    syncMinQtyControl(row);
  }

  async function runInitialSync() {
    beginCheckoutLock();
    try {
      const rows = getCartRows();
      await Promise.all(rows.map((row) => refreshAndSyncAvailabilityMessage(row)));
    } finally {
      endCheckoutLock();
    }
  }

  function bindCartItemsListeners() {
    const cartItems = getCartItemsElement();
    if (!cartItems || cartItems.dataset.cartDetailsAvailabilityBound === "true") {
      return;
    }

    cartItems.dataset.cartDetailsAvailabilityBound = "true";
    cartItems.addEventListener("change", (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.classList.contains("quantity__input")) {
        return;
      }

      const row = input.closest("[data-cart-line]");
      if (row) {
        syncMinQtyControl(row);
      }
    });
  }

  function syncExistingRows() {
    getCartRows().forEach((row) => {
      syncMinQtyControl(row);
    });
  }

  function reinitialize(options = {}) {
    bindCartItemsListeners();
    syncExistingRows();
    syncCheckoutButtonState();

    if (!didRunInitialSync && !options.skipInitialSync) {
      didRunInitialSync = true;
      runInitialSync();
    }
  }

  async function prepareCartChangeContext({ line, quantity, basePayload }) {
    const normalizedQuantity = Number(quantity);
    if (!Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) {
      return { payload: basePayload };
    }

    const row = getRowByLine(line);
    if (!isSupportedRow(row)) {
      return { payload: basePayload };
    }

    const refreshedAvailableQty = await refreshVariantAvailableQuantity(row);
    const result = await computeAvailabilityForRow(
      row,
      normalizedQuantity,
      refreshedAvailableQty
    );
    if (!result.supported) {
      return { payload: basePayload };
    }

    const properties = readLineProperties(row);
    properties._variant_available_quantity = String(refreshedAvailableQty);
    properties.Availability = result.availabilityText;

    const payload = {
      ...basePayload,
      properties,
    };

    const itemKey = row.dataset.cartItemKey;
    const fallbackPayload = itemKey
      ? {
          ...basePayload,
          id: itemKey,
          properties,
        }
      : null;

    if (fallbackPayload) {
      delete fallbackPayload.line;
    }

    return {
      payload,
      fallbackPayload,
      expectedAvailability: result.availabilityText,
    };
  }

  function initialize() {
    reinitialize();

    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS?.cartUpdate &&
      !cartUpdateUnsubscriber
    ) {
      cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        window.requestAnimationFrame(() => {
          reinitialize({ skipInitialSync: true });
        });
      });
    }
  }

  window.tpsCartDetailsPageLogic = {
    beginCheckoutLock,
    prepareCartChangeContext,
    endCheckoutLock,
    reinitialize,
    syncCheckoutButtonState,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
